import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import {
  PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS,
  type PortfolioSmartInsightsAggregationOption,
} from 'lumen/components/market/smart-insights/dropdown-aggregation';
import type { SmartInsightDay } from 'lumen/models';
import {
  SMART_INSIGHT_PRIORITIES,
  type SmartInsightPriority,
} from 'lumen/models/smart-insights/smart-insight-day';
import type MarketSmartInsightsRoute from 'lumen/routes/market/smart-insights';
import type { SmartInsightDayResponseMeta } from 'lumen/serializers/smart-insights/smart-insight-day';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS,
  type PortfolioSmartInsightsSortOption,
} from 'lumen/utils/constants/portfolio-smart-insights-constants';
import {
  SMART_INSIGHTS_ARCHIVE_REASONS,
  SMART_INSIGHTS_TABS,
  type SmartInsightsArchiveReason,
  type SmartInsightsTab,
} from 'lumen/utils/constants/smart-insights';
import type { ModelFrom } from 'lumen/utils/routes';

interface SmartInsightColumn {
  id: string;
  title: string;
}

export default class MarketSmartInsightsController extends Controller {
  declare model: ModelFrom<MarketSmartInsightsRoute>;

  queryParams = ['groupBy', 'priorities', 'smartInsightsTab', 'sortBy'];

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];
  @service declare smartInsights: Services['smart-insights'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked appliedPropertyIds: string[] = [];
  @tracked groupBy: PortfolioSmartInsightsAggregationOption =
    PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS.STAY_DATE;
  @tracked priorities: SmartInsightPriority[] = [
    SMART_INSIGHT_PRIORITIES.HIGH,
    SMART_INSIGHT_PRIORITIES.MEDIUM,
    SMART_INSIGHT_PRIORITIES.LOW,
  ];
  @tracked smartInsightsTab: SmartInsightsTab = SMART_INSIGHTS_TABS.ACTIVE;
  @tracked sortBy: PortfolioSmartInsightsSortOption =
    PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS.PRIORITY;
  filterSmartInsightDayByPriority = (
    smartInsightDay: SmartInsightDay,
  ): boolean => {
    return (
      this.priorities.includes(smartInsightDay.stayDatePriority) &&
      smartInsightDay.insightEntries.length > 0
    );
  };

  @cached
  get allSelectedHotelsHaveFetchError(): boolean {
    const errorIds =
      this.portfolioSmartInsights.smartInsightsHotelIDsWithFetchErrors;
    const selected = this.selectedPropertyIds;

    return (
      selected.length > 0 &&
      errorIds.size > 0 &&
      selected.every((id) => errorIds.has(id))
    );
  }

  get bannerTranslationKey(): string {
    const propertyCount = this.selectedPropertyIds.length;

    if (propertyCount > 100) {
      return tKey(
        'market.smart_insights.banner.uncovering_insights.unlimited_properties',
      );
    }

    return tKey('market.smart_insights.banner.uncovering_insights');
  }

  @cached
  get columns(): SmartInsightColumn[] {
    if (this.isGroupByStayDate) {
      return this.columnsByDate;
    }

    return this.columnsByHotel;
  }

  @cached
  get columnsByDate(): SmartInsightColumn[] {
    let startDate = this.hotelAndMarketService.getLocalTime();
    const endDate = startDate.add(this.portfolioSmartInsights.leadtime, 'day');
    const dates: string[] = [];

    while (startDate.isSameOrBefore(endDate)) {
      dates.push(startDate.format('YYYY-MM-DD'));
      startDate = startDate.add(1, 'day');
    }

    return dates.map((date) => ({
      id: date,
      title: dayjs(date).format('ddd, MMM D'),
    }));
  }

  @cached
  get columnsByHotel(): SmartInsightColumn[] {
    return this.portfolioSmartInsights.filteredSmartInsightsHotels
      .map((hotel) => ({
        id: hotel.hotelinfoId,
        title: hotel.hotelName,
      }))
      .filter((column) => this.selectedPropertyIds.includes(column.id))
      .toSorted((a, b) => a.title.localeCompare(b.title));
  }

  @cached
  get filteredSmartInsights(): SmartInsightDay[] {
    return this.portfolioSmartInsights.smartInsightDays
      .filter((insight) => {
        if (!insight.hotelinfoId) {
          return false;
        }
        return this.portfolioSmartInsights.smartInsightsHotelIDsToDisplay.has(
          insight.hotelinfoId,
        );
      })
      .filter(this.filterSmartInsightDayByPriority);
  }

  @cached
  get insightCountsByStayDate(): Record<string, number> {
    const counts: Record<string, number> = {};

    Object.entries(this.insightsByStayDate).forEach(([stayDate, insights]) => {
      counts[stayDate] = insights.reduce(
        (sum, insight) => sum + insight.insightEntriesSortedByRelevance.length,
        0,
      );
    });

    return counts;
  }

  @cached
  get insightEntriesByHotel(): Record<string, Record<string, SmartInsightDay>> {
    const entriesByHotel: Record<string, Record<string, SmartInsightDay>> = {};
    this.filteredSmartInsights.forEach((smartInsightDay) => {
      const day = smartInsightDay.stayDate;
      const hotelinfoId = smartInsightDay.hotelinfoId;

      if (!hotelinfoId) {
        return;
      }

      if (!entriesByHotel[hotelinfoId]) {
        entriesByHotel[hotelinfoId] = {};
      }

      entriesByHotel[hotelinfoId][day] = smartInsightDay;
    });

    return entriesByHotel;
  }

  @cached
  get insightEntriesByStayDate(): Record<
    string,
    Record<string, SmartInsightDay>
  > {
    const entriesByDay: Record<string, Record<string, SmartInsightDay>> = {};

    Object.entries(this.insightsByStayDate).forEach(([stayDate, insights]) => {
      if (!entriesByDay[stayDate]) {
        entriesByDay[stayDate] = {};
      }
      insights.forEach((smartInsightDay) => {
        const hotelinfoId = smartInsightDay.hotelinfoId;
        if (hotelinfoId && entriesByDay[stayDate]) {
          entriesByDay[stayDate][hotelinfoId] = smartInsightDay;
        }
      });
    });

    return entriesByDay;
  }

  @cached
  get insightsByStayDate(): Record<string, SmartInsightDay[]> {
    const grouped: Record<string, SmartInsightDay[]> = {};

    this.filteredSmartInsights.forEach((insight) => {
      const stayDate = insight.stayDate;
      if (!grouped[stayDate]) {
        grouped[stayDate] = [];
      }
      grouped[stayDate].push(insight);
    });

    return grouped;
  }

  get isGroupByStayDate(): boolean {
    return (
      this.groupBy === PORTFOLIO_SMART_INSIGHTS_AGGREGATION_OPTIONS.STAY_DATE
    );
  }

  get maxInsightEntriesPerDay(): number {
    return Math.max(...Object.values(this.insightCountsByStayDate), 0);
  }

  get myHotelsLoading(): boolean {
    return this.model.myHotels.isRunning;
  }

  get selectedPropertyIds(): string[] {
    return Array.from(
      this.portfolioSmartInsights.smartInsightsHotelIDsToDisplay,
    );
  }

  get selectedSmartInsightDay(): SmartInsightDay | null {
    return this.portfolioSmartInsights.selectedSmartInsightDay;
  }

  get showSmartInsightsSidePanel(): boolean {
    return this.portfolioSmartInsights.selectedSmartInsightDay !== null;
  }

  get smartInsightDays(): SmartInsightDay[] {
    return this.portfolioSmartInsights.smartInsightDays;
  }

  setPriorities(priorities: SmartInsightPriority[]): void {
    this.priorities = priorities;
  }

  @action
  async archiveInsight(reason: SmartInsightsArchiveReason): Promise<void> {
    const smartInsightDay = this.selectedSmartInsightDay;
    if (!smartInsightDay) {
      return;
    }

    const hotelinfoId = smartInsightDay.hotelinfoId;
    const subscriptionId = hotelinfoId
      ? this.portfolioSmartInsights.hotelsByHotelinfoId.get(hotelinfoId)
          ?.subscriptionId
      : undefined;

    try {
      await this.smartInsights.archiveInsightTask.perform(
        smartInsightDay.stayDate,
        reason,
        subscriptionId,
        {
          onRefetch: (stayDate: string, subId?: string): Promise<void> =>
            this.handleUndoArchiveRefetch(stayDate, subId),
          onNavigate: (stayDate: string): void => {
            this.smartInsightsTab = SMART_INSIGHTS_TABS.ACTIVE;
            this.handleUndoArchiveNavigate(stayDate, hotelinfoId);
          },
          product: 'smart-insights-multi-property',
          hotelinfoId,
        },
      );

      this.usageAnalytics.track('Smart Insights Insight Archived', {
        eventTrigger: 'smart-insights-archive-dropdown',
        product: 'smart-insights-multi-property',
        actioned: reason === SMART_INSIGHTS_ARCHIVE_REASONS.ACTION_TAKEN,
        hotelinfoId,
        ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
          smartInsightDay,
          smartInsightDay.stayDate,
        ),
      });

      // Remove the insight from the portfolio tracked array and Ember Data store
      const index =
        this.portfolioSmartInsights.smartInsightDays.indexOf(smartInsightDay);
      if (index !== -1) {
        this.portfolioSmartInsights.smartInsightDays.splice(index, 1);
      }
      this.store.unloadRecord(smartInsightDay);

      // Go back to calendar/list view after archive
      this.collapseSmartInsightsDrawer();
    } catch {
      // Error handled in service
    }
  }

  @action
  collapseSmartInsightsDrawer(): void {
    this.portfolioSmartInsights.setSelectedSmartInsightDay(null);
    this.smartInsightsTab = SMART_INSIGHTS_TABS.ACTIVE;
  }

  @action
  getHasErrorForColumn(columnId: string): boolean {
    if (this.isGroupByStayDate) {
      // Date columns aggregate every selected hotel — only show an error if all
      // failed, otherwise we'd hide insights from hotels that did fetch.
      return this.allSelectedHotelsHaveFetchError;
    }

    return this.portfolioSmartInsights.smartInsightsHotelIDsWithFetchErrors.has(
      columnId,
    );
  }

  @action
  getHasFetchedDataForColumn(columnId: string): boolean {
    if (this.model.myHotels.isRunning) {
      return false;
    }

    if (this.isGroupByStayDate) {
      // For date columns, data is fetched when all selected hotels have been fetched
      return this.selectedPropertyIds.every((id) =>
        this.portfolioSmartInsights.smartInsightsHotelIDsWithFetchedInsights.has(
          id,
        ),
      );
    } else {
      // For hotel columns, check if this specific hotel has been fetched
      return this.portfolioSmartInsights.smartInsightsHotelIDsWithFetchedInsights.has(
        columnId,
      );
    }
  }

  @action
  getInsightDayEntriesForDay(day: string): Record<string, SmartInsightDay> {
    return this.insightEntriesByStayDate[day] || {};
  }

  @action
  getInsightEntriesForColumn(
    columnId: string,
  ): Record<string, SmartInsightDay> {
    if (this.isGroupByStayDate) {
      // Column ID is a date
      return this.insightEntriesByStayDate[columnId] || {};
    } else {
      // Column ID is a hotel ID
      return this.insightEntriesByHotel[columnId] || {};
    }
  }

  @action
  goToSmartInsightDay(stayDate: string): void {
    const element = document.getElementById(stayDate);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
      });
    }
  }

  @action
  handleHotelInsightsCardClick(smartInsightDay: SmartInsightDay): void {
    this.portfolioSmartInsights.setSelectedSmartInsightDay(smartInsightDay);

    const subscriptionId = smartInsightDay.hotelinfoId
      ? this.portfolioSmartInsights.hotelsByHotelinfoId.get(
          smartInsightDay.hotelinfoId,
        )?.subscriptionId
      : undefined;
    this.smartInsights.fetchArchived(smartInsightDay.stayDate, subscriptionId);

    this.usageAnalytics.track('Smart Insights Detail Viewed', {
      eventTrigger: 'smart-insights-calendar',
      product: 'smart-insights-multi-property',
      hotelinfoId: smartInsightDay.hotelinfoId,
      ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
        smartInsightDay,
        smartInsightDay.stayDate,
      ),
    });
  }

  @action
  handleUndoArchiveNavigate(
    stayDate: string,
    hotelinfoId: string | null,
  ): void {
    const restoredInsight = this.portfolioSmartInsights.smartInsightDays.find(
      (sid) => sid.stayDate === stayDate && sid.hotelinfoId === hotelinfoId,
    );

    if (restoredInsight) {
      this.handleHotelInsightsCardClick(restoredInsight);
    }

    this.goToSmartInsightDay(stayDate);
  }

  @action
  async handleUndoArchiveRefetch(
    stayDate: string,
    subscriptionId?: string,
  ): Promise<void> {
    const hotel = this.portfolioSmartInsights.allSmartInsightsHotels.find(
      (h) => h.subscriptionId === subscriptionId,
    );
    if (!hotel) {
      return;
    }

    // Remove existing insights for this hotel to avoid duplicates after re-fetch
    const existingInsights =
      this.portfolioSmartInsights.smartInsightDays.filter(
        (sid) => sid.hotelinfoId === hotel.hotelinfoId,
      );
    for (const insight of existingInsights) {
      const index =
        this.portfolioSmartInsights.smartInsightDays.indexOf(insight);
      if (index !== -1) {
        this.portfolioSmartInsights.smartInsightDays.splice(index, 1);
      }
    }

    // Fetch directly — bypass the enqueued getHotelSmartInsightsTask so undo
    // isn't blocked behind other in-flight hotel fetches.
    const today = dayjs().startOf('day');
    const response = await this.store.query<SmartInsightDay>(
      'smart-insights/smart-insight-day',
      {
        namespace: APIGATEWAY,
        typeKey: 'smartinsights/insights',
        subscription_id: hotel.subscriptionId,
        extract_date: today.format('YYYY-MM-DD'),
        from_date_start: today.format('YYYY-MM-DD'),
        rate_type: 'lowest',
        ota: 'bookingdotcom',
        language: this.hotelAndMarketService.user.locale,
      },
    );

    const meta = response.meta as SmartInsightDayResponseMeta;
    response.forEach((insightDay) => {
      insightDay.hotelinfoId = hotel.hotelinfoId;
      insightDay.meta = meta;
    });

    this.portfolioSmartInsights.smartInsightDays.push(...response);
  }

  @action
  setGroupBy(option: PortfolioSmartInsightsAggregationOption): void {
    this.groupBy = option;

    this.usageAnalytics.track('Smart Insights Grouping Changed', {
      eventTrigger: 'view-switcher',
      product: 'smart-insights-multi-property',
      view: option,
    });
  }

  @action
  setGroupByAndCloseDrawer(option: string): void {
    this.setGroupBy(option as PortfolioSmartInsightsAggregationOption);
    this.collapseSmartInsightsDrawer();
  }

  @action
  setPrioritiesAndCloseDrawer(priorities: SmartInsightPriority[]): void {
    this.setPriorities(priorities);
    this.collapseSmartInsightsDrawer();
  }

  @action
  setSelectedPropertyIds(ids: string[]): void {
    this.portfolioSmartInsights.setSmartInsightsHotelIDsToDisplay(ids);
  }

  @action
  setSmartInsightsTab(tab: SmartInsightsTab): void {
    this.smartInsightsTab = tab;
  }

  @action
  setSortBy(sortOption: PortfolioSmartInsightsSortOption): void {
    this.sortBy = sortOption;

    this.usageAnalytics.track('Data Series Sorted', {
      eventTrigger: 'sort-dropdown',
      product: 'smart-insights-multi-property',
      type: sortOption,
    });
  }

  @action
  setSortByAndCloseDrawer(sortOption: PortfolioSmartInsightsSortOption): void {
    this.setSortBy(sortOption);
    this.collapseSmartInsightsDrawer();
  }
}
